import type { Identifier } from "ra-core";

import type { MaritalPropertyRegime, MaritalStatus } from "./types";

/**
 * Almikia Epic EC2b (TASK-124): types of the contact expediente (sensitive PLD / contract data),
 * with EXACTLY the column names of the TASK-123 tables (`contact_expedientes`,
 * `contact_expediente_documents`). Re-exported from `types.ts`. The `Contact` type is deliberately
 * NOT given any expediente field: the expediente is read only through the audited RPCs.
 */

export const EXPEDIENTE_PERSON_KINDS = [
  "persona_fisica",
  "persona_moral",
] as const;
export type ExpedientePersonKind = (typeof EXPEDIENTE_PERSON_KINDS)[number];

export const EXPEDIENTE_ID_TYPES = [
  "ine",
  "pasaporte",
  "licencia",
  "cedula_profesional",
  "otro",
] as const;
export type ExpedienteIdType = (typeof EXPEDIENTE_ID_TYPES)[number];

export const EXPEDIENTE_PAYMENT_METHODS = [
  "transferencia",
  "cheque",
  "efectivo",
  "credito_hipotecario",
  "otro",
] as const;
export type ExpedientePaymentMethod =
  (typeof EXPEDIENTE_PAYMENT_METHODS)[number];

export const CONTACT_EXPEDIENTE_DOC_TYPES = [
  "identificacion",
  "comprobante_domicilio",
  "acta_constitutiva",
  "poder_representante",
  "pld_soporte",
] as const;
export type ContactExpedienteDocType =
  (typeof CONTACT_EXPEDIENTE_DOC_TYPES)[number];

/** A row of `contact_expedientes`. */
export type ContactExpediente = {
  id: Identifier;
  contact_id: Identifier;
  person_kind: ExpedientePersonKind;
  rfc?: string | null;
  curp?: string | null;
  birth_date?: string | null;
  birth_place?: string | null;
  nationality?: string | null;
  marital_status?: MaritalStatus | null;
  marital_property_regime?: MaritalPropertyRegime | null;
  occupation?: string | null;
  address_street?: string | null;
  address_number?: string | null;
  address_neighborhood?: string | null;
  address_zip?: string | null;
  address_municipality?: string | null;
  address_state?: string | null;
  id_type?: ExpedienteIdType | null;
  id_expires_on?: string | null;
  legal_name?: string | null;
  legal_representative_name?: string | null;
  funds_origin?: string | null;
  payment_method?: ExpedientePaymentMethod | null;
  beneficial_owner_is_self?: boolean | null;
  beneficial_owner_name?: string | null;
  /** null until answered. */
  is_pep?: boolean | null;
  pep_detail?: string | null;
  created_at: string;
  created_by?: Identifier | null;
  updated_at: string;
  updated_by?: Identifier | null;
};

/**
 * A document row as `get_contact_expediente()` returns it (NO storage path: the path is learned
 * only through the audited `get_contact_expediente_document_path()`).
 */
export type ContactExpedienteDocument = {
  id: Identifier;
  doc_type: ContactExpedienteDocType;
  version_number: number;
  file_name: string;
  mime_type: string;
  size_bytes: number;
  uploaded_at: string;
  deleted_at?: string | null;
};

/** What `save_contact_expediente()` accepts: only the writable columns, all optional (partial save). */
export type ContactExpedienteInput = Partial<
  Omit<
    ContactExpediente,
    | "id"
    | "contact_id"
    | "created_at"
    | "created_by"
    | "updated_at"
    | "updated_by"
  >
>;

/** Result of `getContactExpediente`: the expediente (null when none yet) and its documents. */
export type ContactExpedienteBundle = {
  expediente: ContactExpediente | null;
  documents: ContactExpedienteDocument[];
};

/** One contact's missing labels, as `deal_expediente_missing()` returns them. */
export type DealExpedienteMissingEntry = {
  contact_id: Identifier;
  contact_name: string;
  missing: string[];
};

export type UploadContactExpedienteDocumentInput = {
  contactId: Identifier;
  docType: ContactExpedienteDocType;
  file: File;
};
